import {
  Autocomplete,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import type { Moment } from 'moment'
import { useTranslation } from 'react-i18next'

import { type SurveyDefinitionByUserResponse } from '~/api'

import type { SurveyDynamicModel } from '../types/survey-dynamic-model.type'

export interface FilterOption {
  label: string
  value: string
}

interface DynamicLinksFilterPanelProps {
  selectedUser: string
  onUserChange: (user: string) => void
  selectedDateFrom: Moment | null
  onDateFromChange: (date: Moment | null) => void
  selectedDateTo: Moment | null
  onDateToChange: (date: Moment | null) => void
  selectedSurvey: SurveyDefinitionByUserResponse | null
  onSurveyChange: (survey: SurveyDefinitionByUserResponse | null) => void
  isActive: boolean
  onActiveChange: (active: boolean) => void
  surveyDefinitions: SurveyDefinitionByUserResponse[]
  dynamicLinks: SurveyDynamicModel[]
}

export function DynamicLinksFilterPanel({
  selectedUser,
  onUserChange,
  selectedDateFrom,
  onDateFromChange,
  selectedDateTo,
  onDateToChange,
  selectedSurvey,
  onSurveyChange,
  isActive,
  onActiveChange,
  surveyDefinitions,
  dynamicLinks,
}: DynamicLinksFilterPanelProps) {
  const { t } = useTranslation()

  // Extract unique users from dynamic links
  const userOptions = useMemo<FilterOption[]>(() => {
    const allOption: FilterOption = {
      label: t('views.dynamicLinks.allUsers'),
      value: '',
    }
    if (!dynamicLinks || dynamicLinks.length === 0) return [allOption]

    const uniqueUsers = Array.from(
      new Set(
        dynamicLinks
          .map(link => link.createdBy)
          .filter((user): user is string => Boolean(user)),
      ),
    ).map(user => ({
      label: user,
      value: user,
    }))

    return [allOption, ...uniqueUsers]
  }, [dynamicLinks, t])

  function handleUserChange(_event: unknown, newValue: FilterOption | null) {
    onUserChange(newValue?.value ?? '')
  }

  function handleDateFromChange(newValue: Moment | null) {
    onDateFromChange(newValue)
  }

  function handleDateToChange(newValue: Moment | null) {
    onDateToChange(newValue)
  }

  function handleSurveyChange(
    _event: unknown,
    newValue: SurveyDefinitionByUserResponse | null,
  ) {
    onSurveyChange(newValue)
  }

  const selectedUserOption =
    userOptions.find(option => option.value === selectedUser) ?? userOptions[0]

  return (
    <Stack gap={2}>
      <Stack flexDirection="row" alignItems="center" flexWrap="wrap" gap={2}>
        <Autocomplete
          options={userOptions}
          value={selectedUserOption}
          onChange={handleUserChange}
          getOptionLabel={option => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          size="small"
          sx={{
            minWidth: 200,
            '& .MuiOutlinedInput-root': { borderRadius: '20px' },
          }}
          renderInput={params => (
            <TextField {...params} label={t('views.dynamicLinks.user')} />
          )}
        />

        <DatePicker
          label={t('views.dateRangeFilter.from')}
          value={selectedDateFrom}
          onChange={handleDateFromChange}
          maxDate={selectedDateTo ?? undefined}
          slotProps={{
            textField: {
              size: 'small',
              sx: {
                minWidth: 200,
                '& .MuiOutlinedInput-root': { borderRadius: '20px' },
              },
            },
          }}
        />

        <DatePicker
          label={t('views.dateRangeFilter.to')}
          value={selectedDateTo}
          onChange={handleDateToChange}
          minDate={selectedDateFrom ?? undefined}
          slotProps={{
            textField: {
              size: 'small',
              sx: {
                minWidth: 200,
                '& .MuiOutlinedInput-root': { borderRadius: '20px' },
              },
            },
          }}
        />

        <Stack direction="row" alignItems="center" gap={1} ml="auto">
          <Typography variant="body2" color="text.secondary">
            {t('views.projects.form.inactive')}
          </Typography>
          <Switch
            checked={isActive}
            onChange={e => {
              onActiveChange(e.target.checked)
            }}
          />
          <Typography variant="body2" color="text.secondary">
            {t('views.projects.form.active')}
          </Typography>
        </Stack>
      </Stack>

      <Autocomplete
        options={surveyDefinitions}
        value={selectedSurvey}
        onChange={handleSurveyChange}
        getOptionLabel={option => option.title}
        isOptionEqualToValue={(option, value) =>
          option.description === value.description
        }
        size="small"
        fullWidth
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '20px' } }}
        renderInput={params => (
          <TextField
            {...params}
            label={t('views.dynamicLinks.surveyDefinition')}
          />
        )}
      />
    </Stack>
  )
}
